import * as _ from 'lodash';
import { Col, ControlLabel, FormControl, FormGroup, Panel, Row } from 'react-bootstrap';
import { HlaAntigenSelectionForm, HlaDrb345ToggleForm } from '@nkr/components/form/hla';
import { IRecipientProfileHlaInfo, IRecipientProfileHlaValues } from '@nkr/models/index';
import { HlaLocusGroup } from '@nkr/models/hla';

interface IProps {
    data: IRecipientProfileHlaInfo,
    hlaValueList: IRecipientProfileHlaValues[],
    handleHlaChange: (name : string, value : string | number | boolean | null) => void
}

const HLAClass1 = (props: IProps) => {
    const aAntigens  = props.hlaValueList.find(x => x.hla == 'A');
    const bAntigens  = props.hlaValueList.find(x => x.hla == 'B');
    const bwAntigens = props.hlaValueList.find(x => x.hla == 'Bw');
    const cwAntigens = props.hlaValueList.find(x => x.hla == 'Cw');
    const alleles = props.data.alleles ?? [];

    const handleNumberChange = (e: React.ChangeEvent<HTMLSelectElement & HTMLInputElement>) => {
        props.handleHlaChange(e.target.name, parseInt(e.target.value));
    };

    const handleTextChange = (e: React.ChangeEvent<FormControl & HTMLInputElement>) => {
        props.handleHlaChange(e.currentTarget.name, e.currentTarget.value);
    };

    if (!props.data || props.hlaValueList.length == 0) {
        return <></>
    }

    return (
        <>
            <Col xs={12} lg={6} style={{ display: 'flex', flexDirection: 'column' }}>
                <Panel bsStyle='primary' style={{ borderColor: '#e7f1fa', flex: 1 }}>
                    <Panel.Heading style={{
                        backgroundColor: '#1e3a5f',
                        borderColor: '#1e3a5f',
                        color: '#fff',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        letterSpacing: '1px'
                    }}>
                        HLA Class I
                    </Panel.Heading>
                    <Panel.Body style={{ padding: '6px' }}>

                <HlaAntigenSelectionForm
                    antigen1Name={'hlaA1'}
                    antigen1Value={props.data.hlaA1}
                    antigen2Name={'hlaA2'}
                    antigen2Value={props.data.hlaA2}
                    alleles={alleles.filter(x => x.locusGroup == HlaLocusGroup.A)}
                    antigenOptions={aAntigens.antigens}
                    title={'A'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />
                
                <HlaAntigenSelectionForm
                    antigen1Name={'hlaB1'}
                    antigen1Value={props.data.hlaB1}
                    antigen2Name={'hlaB2'}
                    antigen2Value={props.data.hlaB2}
                    alleles={alleles.filter(x => x.locusGroup == HlaLocusGroup.B)}
                    antigenOptions={bAntigens.antigens}
                    title={'B'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />
                
                <HlaAntigenSelectionForm
                    antigen1Name={'hlaBw1'}
                    antigen1Value={props.data.hlaBw1}
                    antigen2Name={'hlaBw2'}
                    antigen2Value={props.data.hlaBw2}
                    alleles={[]}
                    antigenOptions={bwAntigens.antigens}
                    title={'Bw'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />
                
                <HlaAntigenSelectionForm
                    antigen1Name={'hlaC1'}
                    antigen1Value={props.data.hlaC1}
                    antigen2Name={'hlaC2'}
                    antigen2Value={props.data.hlaC2}
                    alleles={alleles.filter(x => x.locusGroup == HlaLocusGroup.C)}
                    antigenOptions={cwAntigens.antigens}
                    title={'Cw'}
                    antigenLabel={true}
                    equivalentSignedOff={props.data.hlaDiscrepancyIsEquivalent}
                    handleAntigenChange={handleNumberChange}
                />
                
                <Row>
                    <div style={{ fontSize: '15px', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px', marginBottom: '30px' }}>
                        * Allele data is populated from&nbsp;<b>"NKR High Resolution Tissue Typing"</b>
                    </div>
                </Row>

                <FormGroup>
                    <Col xs={3} lg={2} componentClass={ControlLabel} style={{ textAlign: 'left' }}>Last Reviewed Date:</Col>
                    <Col xs={5} lg={4}>
                        <FormControl
                            type='date'
                            name='labReviewDate'
                            value={props.data.labReviewDate ?? ''}
                            max={new Date().toISOString().split('T')[0]}
                            onChange={handleTextChange}
                        />
                    </Col>
                </FormGroup>

                <FormGroup>
                    <Col xs={3} lg={2} componentClass={ControlLabel} style={{ textAlign: 'left' }}>Your Initials:</Col>
                    <Col xs={5} lg={4}>
                        <FormControl
                            type='text'
                            name='labReviewInitials'
                            maxLength={3}
                            value={props.data.labReviewInitials ?? ''}
                            onChange={handleTextChange}
                        />
                    </Col>
                </FormGroup>

                {props.data.reviewHistoryList?.length > 0 && (
                    <FormGroup>
                        <Col xs={5} lg={4} componentClass={ControlLabel} style={{ textAlign: 'left' }}>Review History:</Col>
                        <Col xs={7} lg={8}>
                            <table
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    fontFamily: "Arial, sans-serif",
                                    border: "1px solid #ccc"
                                }}
                            >
                                <thead>
                                    <tr
                                        style={{
                                            backgroundColor: "#999999",
                                            color: "white",
                                            textAlign: "left",
                                            borderBottom: "1px solid #ccc"
                                        }}
                                    >
                                        <th style={{ padding: "6px", width: "200px" }}>Review</th>
                                        <th style={{ padding: "6px" }}>Log Date</th>
                                        <th style={{ padding: "6px" }}>Review Date</th>
                                        <th style={{ padding: "6px" }}>Initials</th>
                                        <th style={{ padding: "6px" }}>User</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {props.data.reviewHistoryList.map((item, i) => {
                                        const rowColor = i % 2 == 0 ? "#eeeeee" : "white";

                                        return (
                                            <tr
                                                key={"review-" + item.id}
                                                style={{
                                                    backgroundColor: rowColor,
                                                    borderBottom: "1px solid #ccc"
                                                }}
                                            >
                                                <td style={{ padding: "6px", fontWeight: "bold" }}>{item.reviewType}</td>
                                                <td style={{ padding: "6px" }}>{item.logDateTime}</td>
                                                <td style={{ padding: "6px" }}>{item.reviewedDateTime}</td>
                                                <td style={{ padding: "6px" }}>{item.reviewedByInitials}</td>
                                                <td style={{ padding: "6px" }}>{item.reviewedByName}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </Col>
                    </FormGroup>
                )}

                    </Panel.Body>
                </Panel>
            </Col>
        </>
    )
}

export default HLAClass1;